
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const AIAssessmentPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    company: '',
    email: '',
    industry: '',
    company_size: '',
    current_marketing: '',
    ai_experience: '',
    main_challenges: '',
    goals: '',
    budget_range: '',
    timeline: ''
  });
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const totalSteps = 3;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateScore = () => {
    let totalScore = 0;
    
    // Industry scoring
    if (formData.industry === 'technology' || formData.industry === 'ecommerce') totalScore += 20;
    else if (formData.industry === 'healthcare' || formData.industry === 'finance') totalScore += 15;
    else totalScore += 10;
    
    // Company size scoring
    if (formData.company_size === 'large') totalScore += 20;
    else if (formData.company_size === 'medium') totalScore += 15;
    else totalScore += 10;
    
    // Current marketing scoring
    if (formData.current_marketing === 'advanced') totalScore += 20;
    else if (formData.current_marketing === 'moderate') totalScore += 15;
    else totalScore += 5;
    
    // AI experience scoring
    if (formData.ai_experience === 'experienced') totalScore += 20;
    else if (formData.ai_experience === 'some') totalScore += 15;
    else totalScore += 10;
    
    // Budget scoring
    if (formData.budget_range === 'high') totalScore += 20;
    else if (formData.budget_range === 'medium') totalScore += 15;
    else totalScore += 10;
    
    setScore(totalScore);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    calculateScore();
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d44s6qn0vhksjg8rfn2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });
      
      if (response.ok) {
        setShowResults(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setShowResults(true); // Show results anyway
    }
  };

  const getRecommendation = () => {
    if (score >= 80) {
      return {
        level: 'High AI Readiness',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        description: 'Your business is highly ready for AI implementation. You have the infrastructure, experience, and resources to maximize AI marketing benefits.',
        recommendations: [
          'Implement advanced conversational AI systems',
          'Deploy predictive analytics for customer behavior',
          'Automate complex marketing workflows',
          'Integrate AI across all customer touchpoints'
        ]
      };
    } else if (score >= 60) {
      return {
        level: 'Moderate AI Readiness',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        description: 'Your business has good potential for AI adoption with some preparation. Focus on building foundational capabilities first.',
        recommendations: [
          'Start with basic marketing automation',
          'Implement AI-powered lead scoring',
          'Develop data collection strategies',
          'Train team on AI marketing tools'
        ]
      };
    } else {
      return {
        level: 'Building AI Foundation',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        description: 'Your business would benefit from establishing AI fundamentals before advanced implementation. Start with basic automation.',
        recommendations: [
          'Begin with simple chatbot implementation',
          'Establish data collection processes',
          'Invest in team AI education',
          'Start with basic email automation'
        ]
      };
    }
  };

  if (showResults) {
    const recommendation = getRecommendation();
    
    return (
      <div className="min-h-screen bg-white">
        <Header />
        
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Your AI Readiness Assessment Results</h1>
              <div className="text-6xl font-bold mb-4 text-blue-600">{score}/100</div>
              <div className={`inline-block px-6 py-3 rounded-full text-lg font-semibold ${recommendation.bgColor} ${recommendation.color}`}>
                {recommendation.level}
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Assessment Summary</h2>
              <p className="text-gray-600 mb-6">{recommendation.description}</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Recommended Next Steps:</h3>
              <ul className="space-y-2 mb-8">
                {recommendation.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 mt-1"></i>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Get Started?</h3>
                <p className="text-gray-600 mb-4">
                  Schedule a free consultation to discuss your AI marketing strategy and implementation plan.
                </p>
                <button className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  Schedule Free Consultation
                </button>
              </div>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">AI Readiness Assessment</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover how ready your business is for AI marketing implementation and get personalized recommendations.
            </p>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Step {currentStep} of {totalSteps}</span>
              <span className="text-sm font-medium text-gray-700">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} data-readdy-form className="bg-white rounded-xl shadow-lg p-8">
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Company Information</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Industry</option>
                    <option value="technology">Technology</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail</option>
                    <option value="services">Professional Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
                  <select
                    name="company_size"
                    value={formData.company_size}
                    onChange={(e) => handleInputChange('company_size', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Company Size</option>
                    <option value="small">1-50 employees</option>
                    <option value="medium">51-200 employees</option>
                    <option value="large">200+ employees</option>
                  </select>
                </div>
              </div>
            )}
            
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Current Marketing & AI Experience</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Marketing Sophistication</label>
                  <select
                    name="current_marketing"
                    value={formData.current_marketing}
                    onChange={(e) => handleInputChange('current_marketing', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Level</option>
                    <option value="basic">Basic (Email, Social Media)</option>
                    <option value="moderate">Moderate (CRM, Analytics, Automation)</option>
                    <option value="advanced">Advanced (Multi-channel, Attribution, Personalization)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">AI Experience Level</label>
                  <select
                    name="ai_experience"
                    value={formData.ai_experience}
                    onChange={(e) => handleInputChange('ai_experience', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Experience</option>
                    <option value="none">No AI experience</option>
                    <option value="some">Some AI tools or experiments</option>
                    <option value="experienced">Experienced with AI implementation</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Main Marketing Challenges</label>
                  <select
                    name="main_challenges"
                    value={formData.main_challenges}
                    onChange={(e) => handleInputChange('main_challenges', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Primary Challenge</option>
                    <option value="lead_generation">Lead Generation</option>
                    <option value="customer_retention">Customer Retention</option>
                    <option value="personalization">Personalization at Scale</option>
                    <option value="roi_measurement">ROI Measurement</option>
                    <option value="automation">Marketing Automation</option>
                    <option value="data_analysis">Data Analysis & Insights</option>
                  </select>
                </div>
              </div>
            )}
            
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Goals & Investment</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary AI Marketing Goals</label>
                  <select
                    name="goals"
                    value={formData.goals}
                    onChange={(e) => handleInputChange('goals', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Primary Goal</option>
                    <option value="increase_leads">Increase Lead Generation</option>
                    <option value="improve_conversion">Improve Conversion Rates</option>
                    <option value="reduce_costs">Reduce Marketing Costs</option>
                    <option value="enhance_personalization">Enhance Personalization</option>
                    <option value="automate_processes">Automate Marketing Processes</option>
                    <option value="better_insights">Gain Better Customer Insights</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Marketing Budget Range</label>
                  <select
                    name="budget_range"
                    value={formData.budget_range}
                    onChange={(e) => handleInputChange('budget_range', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Budget Range</option>
                    <option value="low">Under $5,000</option>
                    <option value="medium">$5,000 - $20,000</option>
                    <option value="high">$20,000+</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Implementation Timeline</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select Timeline</option>
                    <option value="immediate">Immediate (Within 1 month)</option>
                    <option value="short">Short-term (1-3 months)</option>
                    <option value="medium">Medium-term (3-6 months)</option>
                    <option value="long">Long-term (6+ months)</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="flex justify-between mt-8">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Previous
                </button>
              )}
              
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="ml-auto px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  className="ml-auto px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get My Assessment
                </button>
              )}
            </div>
          </form>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AIAssessmentPage;
